import {
  AwaitContainer,
  Button,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  ScrollArea,
  Separator,
} from '@ibsys/kit';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { ListTruncationNotice } from '../ListTruncationNotice';
import { LiveSearch } from '../LiveSearch';
import { SelectionDialogRow } from './SelectionDialogRow';

/** Тексты окна выбора */
export type SelectionDialogLabels = {
  /** Подсказка в поле поиска; по умолчанию — общая подпись поиска */
  searchPlaceholder?: string;
  /** Пустая выдача без запроса: выбирать не из чего */
  empty: string;
  /** Пустая выдача по запросу; по умолчанию — общее «ничего не найдено» */
  notFound?: string;
  /** Кнопка подтверждения — называет действие окна */
  confirm: string;
};

type Props<T> = {
  open: boolean;
  /** Открытие и закрытие окна — триггером, крестиком, «Отменить», Esc, щелчком вне окна */
  onOpenChange: (open: boolean) => void;
  /** Триггер открытия (`DialogTrigger asChild`); без него окно открывают снаружи через `open` */
  trigger?: ReactNode;
  title: ReactNode;
  /** Набранный запрос */
  search: string;
  /** Новый запрос — на каждый набранный символ */
  onSearchChange: (value: string) => void;
  /** Дополнительный отбор окна под полем поиска */
  filters?: ReactNode;
  /** Записи под текущий запрос; отмеченные окно из них исключает само */
  items: T[];
  /** Отмеченные записи в порядке отметки */
  selected: T[];
  onToggle: (item: T, isChecked: boolean) => void;
  /** Ключ записи: по нему выдача сопоставляется с отмеченными */
  getItemId: (item: T) => string | number;
  /** Название записи — подпись флажка */
  renderTitle: (item: T) => ReactNode;
  /** Отличительный признак записи справа в строке */
  renderHint?: (item: T) => ReactNode;
  /** Описание записи строкой под названием */
  renderDescription?: (item: T) => ReactNode;
  isLoading?: boolean;
  /** Сколько записей под запросом всего, если `items` — лишь первое окно выдачи */
  total?: number;
  labels: SelectionDialogLabels;
  onConfirm: () => void;
  /**
   * Подтверждать нечего; без пропа — пока не отмечена ни одна запись. Окно, открытое с текущим
   * составом отмеченным, передаёт «отметки совпадают с составом»
   */
  confirmDisabled?: boolean;
  /** Подтверждение выполняется */
  isConfirming?: boolean;
};

/**
 * Окно выбора из списка: поиск, отмеченные записи группой над выдачей и подтверждение выбора.
 *
 * Компонент управляемый: что показать, как искать и что делать с выбором, решает потребитель.
 * Отмеченные держатся отдельной группой, поэтому отметка переживает смену запроса и снимается,
 * даже когда записи в выдаче уже нет.
 */
export const SelectionDialog = <T,>({
  open,
  onOpenChange,
  trigger,
  title,
  search,
  onSearchChange,
  filters,
  items,
  selected,
  onToggle,
  getItemId,
  renderTitle,
  renderHint,
  renderDescription,
  isLoading = false,
  total,
  labels,
  onConfirm,
  confirmDisabled = selected.length === 0,
  isConfirming = false,
}: Props<T>) => {
  const { t } = useTranslation('common');

  const selectedIds = new Set(selected.map(getItemId));
  const rest = items.filter((item) => !selectedIds.has(getItemId(item)));

  const getStatusText = () => {
    if (items.length === 0) return search.trim() ? (labels.notFound ?? t('noItemsFound')) : labels.empty;
    if (rest.length === 0) return t('allFoundSelected');
    return null;
  };
  const statusText = getStatusText();

  const renderRow = (item: T, isChecked: boolean) => (
    <SelectionDialogRow
      key={getItemId(item)}
      title={renderTitle(item)}
      hint={renderHint?.(item)}
      description={renderDescription?.(item)}
      isChecked={isChecked}
      onToggle={(value) => onToggle(item, value)}
    />
  );

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="min-w-0">
          {/* Крестик окна стоит поверх содержимого: место под него оставляет сам заголовок */}
          <DialogTitle className="pr-6 wrap-anywhere">
            <span className="leading-snug">{title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-3">
          {/* Выдача следует за набором: иначе первый щелчок после набора перестраивает список под курсором
              и теряется */}
          <LiveSearch value={search} onChange={onSearchChange} placeholder={labels.searchPlaceholder ?? t('search')} />
          {filters}
          <ScrollArea
            orientation="vertical"
            className={SCROLL_AREA_VIEWPORT_BLOCK}
            viewPortProps={{ className: 'max-h-96' }}
          >
            <div className="flex min-w-0 flex-col gap-2">
              {selected.length > 0 && (
                <>
                  <p className="text-sm font-medium">{t('selectedCount', { count: selected.length })}</p>
                  {selected.map((item) => renderRow(item, true))}
                  <Separator className="my-1" />
                </>
              )}
              <AwaitContainer isLoading={isLoading} className="flex min-w-0 flex-col gap-2">
                {statusText && <p className="text-sm text-muted-foreground">{statusText}</p>}
                {rest.map((item) => renderRow(item, false))}
              </AwaitContainer>
            </div>
          </ScrollArea>
          <ListTruncationNotice shown={items.length} total={total} />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('cancel')}</Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={confirmDisabled} isLoading={isConfirming}>
            {labels.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
